// FILE: src/access/ManagerPageGate.jsx
// Route guard for Account Managers.
//
// When manager-mode is active, we subscribe to the manager's grant
// (grants/{ownerUid}_{managerUid}) and block navigation to any pageKey
// that is not permitted.
//
// Account Managers are ALWAYS read-only; this gate is UI-level protection.
// Firestore rules must also enforce page-level read access.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { getManagerOwner, onManagerModeChange } from "../lib/managerMode";
import { PAGE_ACCESS, canManagerSeePage } from "./pageAccess";

function prettyLabel(key) {
  return PAGE_ACCESS.find((p) => p.key === key)?.label || key;
}

export default function ManagerPageGate({ pageKey, children }) {
  const navigate = useNavigate();

  const [mm, setMm] = useState(() => getManagerOwner());
  useEffect(() => onManagerModeChange(setMm), []);

  const isManagerMode = !!mm && !!mm.ownerUid && !!mm.managerUid;

  const grantDocId = useMemo(() => {
    if (!isManagerMode) return null;
    return `${mm.ownerUid}_${mm.managerUid}`;
  }, [isManagerMode, mm?.ownerUid, mm?.managerUid]);

  const [loading, setLoading] = useState(isManagerMode && !!pageKey);
  const [allowed, setAllowed] = useState(!isManagerMode || !pageKey);
  const [error, setError] = useState("");

  useEffect(() => {
    // Not in manager mode? never block.
    if (!isManagerMode) {
      setLoading(false);
      setAllowed(true);
      setError("");
      return;
    }

    // No pageKey? never block.
    if (!pageKey || !grantDocId) {
      setLoading(false);
      setAllowed(true);
      setError("");
      return;
    }

    setLoading(true);
    setAllowed(false);
    setError("");

    const unsub = onSnapshot(
      doc(db, "grants", grantDocId),
      (snap) => {
        const grant = snap.exists() ? snap.data() || {} : null;
        setAllowed(!!canManagerSeePage(grant, pageKey));
        setLoading(false);
      },
      (e) => {
        setAllowed(false);
        setLoading(false);
        setError(e?.message || "Unable to verify access.");
      }
    );

    return () => unsub();
  }, [isManagerMode, pageKey, grantDocId]);

  if (!isManagerMode) return children || null;

  if (loading) {
    return (
      <div style={{ padding: 18 }}>
        <div style={{ fontWeight: 900, color: "#2a0516" }}>Checking access…</div>
        <div style={{ color: "#657899", marginTop: 6 }}>Please wait.</div>
      </div>
    );
  }

  if (allowed) return children || null;

  return (
    <div style={{ padding: 18 }}>
      <div style={{ fontWeight: 1000, color: "#2a0516", fontSize: 18 }}>
        Access restricted
      </div>

      <div style={{ color: "#657899", marginTop: 6, lineHeight: 1.4 }}>
        You don’t have permission to view <b>{prettyLabel(pageKey)}</b> for this account.
      </div>

      {error ? (
        <div style={{ color: "#9ca3af", marginTop: 8, fontSize: 12 }}>{error}</div>
      ) : null}

      <div style={{ color: "#657899", marginTop: 10 }}>
        Ask the account owner (or an administrator) to update your permissions.
      </div>

      <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => navigate("/manager-select")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "10px 14px",
            borderRadius: 12,
            background: "#2a0516",
            color: "#fff",
            border: "none",
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          Back to Managed Accounts
        </button>

        <button
          type="button"
          onClick={() => navigate(`/support?request=page_access&page=${encodeURIComponent(pageKey)}`)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "10px 14px",
            borderRadius: 12,
            background: "#645155",
            color: "#fff",
            border: "none",
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          Request access
        </button>
      </div>
    </div>
  );
}
